import * as React from 'react'

import {
  Box,
  CHART_COLOR_NAMES,
  type Colors,
  Text,
  colors as gammaColors,
} from '@factorialco/deprecated-design-system'
import { ResponsiveBar } from '@nivo/bar'

import BarItem, { StackProvider } from './BarItem'
import LegendItem from './LegendItem'

export type CustomTooltipProps = {
  label: string
  value: number
  color: Colors
  id: string
  index: number
}

type ResponsiveBarProps = React.ComponentProps<typeof ResponsiveBar>

type Margin = {
  top: number
  bottom: number
  left: number
  right: number
}

// Analysis data for stacked bars to determine segment positions
type StackAnalysis = Record<
  string,
  {
    existingSegments: string[]
    firstSegment?: string
    lastSegment?: string
  }
>

type BarChartProps = {
  tooltip?: React.FC<CustomTooltipProps>
  indexBy: string
  data: Record<string, number | string>[]
  keys: string[]
  colors?: Colors[]
  maxValue: number | 'auto'
  margin?: Margin
  axisBottom?: ResponsiveBarProps['axisBottom']
  colorBy?: 'id' | 'indexValue'
  enabledGridY?: boolean
  onClick?: (datum: { index: number; label: string; value: number | null }) => void
  isInteractive?: boolean
  pointer?: boolean
  groupMode?: 'grouped' | 'stacked'
  showLegend?: boolean
  useCustomTooltip?: boolean
  selectableBars?: boolean
}

// Theme configuration for chart styling
const theme = {
  grid: {
    line: {
      stroke: '#dddddd',
      strokeWidth: 1,
      strokeDasharray: '4 4',
    },
  },
  axis: {
    ticks: {
      line: {
        strokeWidth: 0,
      },
    },
  },
}

const AdditionalItemsPopover: React.FC<{
  items: Array<{ key: string; color: Colors }>
}> = ({ items }) => {
  return (
    <Box
      style={{
        position: 'absolute',
        top: '100%',
        right: '0',
        zIndex: '1000',
        backgroundColor: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        marginTop: '4px',
        maxHeight: '200px',
        overflowY: 'auto',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Box flexDirection="column" gap="s8">
        {items.map(({ key, color }) => (
          <LegendItem key={key} color={color}>
            {key}
          </LegendItem>
        ))}
      </Box>
    </Box>
  )
}

const BarChart = ({
  data,
  keys,
  colors = [...CHART_COLOR_NAMES],
  indexBy,
  maxValue,
  margin,
  tooltip,
  axisBottom,
  colorBy,
  enabledGridY,
  onClick,
  isInteractive = true,
  groupMode = 'grouped',
  showLegend = false,
  useCustomTooltip = false,
  selectableBars = true,
}: BarChartProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = React.useState(keys.length)
  const [showPopover, setShowPopover] = React.useState(false)

  const estimatedItemWidth = 120
  const maxRows = 2

  React.useLayoutEffect(() => {
    const node = containerRef.current
    if (!node) return

    const update = () => {
      const width = node.offsetWidth
      const itemsPerRow = Math.floor(width / estimatedItemWidth)
      const rawVisibleCount = itemsPerRow * maxRows
      const adjustedCount = keys.length > rawVisibleCount ? rawVisibleCount - 1 : rawVisibleCount
      setVisibleCount(adjustedCount)
    }

    update()

    const resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(node)

    return () => resizeObserver.disconnect()
  }, [keys.length])

  const visibleKeys = keys.slice(0, visibleCount)
  const hiddenKeys = keys.slice(visibleCount)

  const visibleLegendItems = visibleKeys.map((key, i) => (
    <LegendItem key={key} color={colors[i % colors.length] || 'viridian400'}>
      {key}
    </LegendItem>
  ))

  const additionalItems = hiddenKeys.map((key, i) => ({
    key,
    color: colors[(visibleCount + i) % colors.length] || 'viridian400',
  }))

  // Analyze stacked data to determine first and last segments for rounded corners
  const stackAnalysis = React.useMemo<StackAnalysis | null>(() => {
    if (groupMode !== 'stacked') {
      return null
    }

    return data.reduce<StackAnalysis>((acc, item) => {
      const existingSegments = keys.filter((key) => {
        const value = item[key]
        return typeof value === 'number' && value > 0
      })

      const category = String(item[indexBy])
      acc[category] = {
        existingSegments,
        firstSegment: existingSegments[0],
        lastSegment: existingSegments[existingSegments.length - 1],
      }

      return acc
    }, {})
  }, [data, keys, indexBy, groupMode])

  const overridedTheme = {
    ...theme,
    ...(isInteractive
      ? {
          tooltip: {
            container: {
              cursor: 'pointer',
            },
          },
        }
      : {}),
  }

  React.useEffect(() => {
    const handleClickOutside = () => {
      if (showPopover) setShowPopover(false)
    }

    if (showPopover) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [showPopover])

  return (
    <StackProvider key={`${indexBy}-${data.length}`} groupMode={groupMode}>
      {showLegend && (
        <Box style={{ width: '100%', position: 'relative' }}>
          <Box
            ref={containerRef}
            flexWrap="wrap"
            flexDirection="row"
            gap="s12"
            style={{
              width: '100%',
              minHeight: '64', // Always reserve height for 2 rows of legend items
            }}
          >
            {visibleLegendItems}

            {additionalItems.length > 0 && (
              <Box
                style={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor: showPopover ? '#f0f0f0' : 'transparent',
                  border: '1px solid #e0e0e0',
                  transition: 'background-color 0.2s',
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  setShowPopover((prev) => !prev)
                }}
              >
                <Text.Normal size="100" color="grey600">
                  +{additionalItems.length}
                </Text.Normal>
              </Box>
            )}
          </Box>

          {showPopover && additionalItems.length > 0 && (
            <AdditionalItemsPopover items={additionalItems} />
          )}
        </Box>
      )}

      <ResponsiveBar
        tooltip={useCustomTooltip ? () => null : undefined}
        margin={margin}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legendPosition: 'middle',
          legendOffset: -40,
          tickValues: 4,
        }}
        gridYValues={4}
        colors={colors.map((color) => gammaColors[color])}
        theme={overridedTheme}
        maxValue={maxValue}
        minValue={0}
        enableLabel={false}
        axisBottom={axisBottom}
        groupMode={groupMode}
        borderRadius={5}
        keys={keys}
        indexBy={indexBy}
        data={data}
        padding={0.6}
        innerPadding={0}
        animate={false}
        barComponent={(props) => {
          return (
            <BarItem
              {...props}
              keys={keys}
              groupMode={groupMode}
              stackAnalysis={stackAnalysis}
              originalData={data}
              isInteractive={isInteractive}
              customTooltip={tooltip}
              colors={colors}
              selectableBars={selectableBars}
            />
          )
        }}
        labelTextColor="inherit:darker(1.6)"
        colorBy={colorBy}
        enableGridY={enabledGridY}
        onClick={(datum) =>
          onClick?.({
            label: `${datum.indexValue}`,
            index: datum.index,
            value:
              groupMode === 'stacked'
                ? keys.reduce((acc, key) => {
                    const val = data[datum.index]?.[key]
                    return acc + (typeof val === 'number' ? val : 0)
                  }, 0)
                : datum.value,
          })
        }
        isInteractive={isInteractive}
      />
    </StackProvider>
  )
}

export default BarChart
